<template>
    <div class="px-3" v-if="!details">
        <div v-if="!isNewEvent">
            <div class="loader" v-if="setLoading"></div>
            <div class="card mt-5" v-if="hasCriteriaItems || (canSaveSet)">
                <div class="card-body">
                    <div class="card-deck"
                        v-if="hasCriteriaItems"
                         v-for="criteria in criteriaSet.criteria">
                        <criteria :model="criteria"
                                  :is-locked-for-edit="isLockedForEdit"
                                  :criteriaTypes="criteriaTypes"
                                  @delete="deleteCriteria">
                        </criteria>
                    </div>

                    <div class="m-4" v-if="canSaveSet && hasCriteriaItems">
                        <div class="form-row">
                            <div class="form-group form-check col-md-4 col-sm-12 col-xl-2">
                                <input type="checkbox" class="form-check-input"
                                       v-model="criteriaSet.isTemplate"
                                       :name="'isTemplate' + _uid"
                                       :id="'isTemplate' + _uid">
                                <label class="form-check-label font-weight-bold"
                                       :for="'isTemplate' + _uid">{{
                                    $t('registers.events.evaluation.criteria.saveAsTemplate') }}</label>
                            </div>
                            <div class="form-group col-md-4 col-sm-12 col-xl-5"
                                 v-if="criteriaSet.isTemplate">
                                <label :class="criteriaSet.isTemplate ? 'required': ''"
                                       :for="'criteriaSetName' + _uid">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.name') }}</label>
                                <input type="text" class="form-control"
                                       v-model="criteriaSet.name"
                                       v-validate="criteriaSet.isTemplate ? 'required' : ''"
                                       :data-vv-as="$t('registers.events.evaluation.criteria.gridHeaders.name')"
                                       :id="'criteriaSetName' + _uid"
                                       :name="'criteriaSetName' + _uid">
                                <span class="text-danger" v-if="errors.has('criteriaSetName' + _uid)">{{ errors.first('criteriaSetName' + _uid) }}</span>
                            </div>
                            <div class="form-group col-md-4 col-sm-12 col-xl-5"
                                 v-if="criteriaSet.isTemplate">
                                <label :for="'criteriaSetNameEn' + _uid">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.nameEn') }}</label>
                                <input type="text" class="form-control"
                                       v-model="criteriaSet.nameEn"
                                       :id="'criteriaSetNameEn' + _uid"
                                       :name="'criteriaSetNameEn' + _uid">
                            </div>
                        </div>
                        <div class="text-center">
                            <button type="button" class="btn btn-primary btn-lg"
                                    :class="savingSet ? 'disabled' : ''"
                                    @click="saveCriteriaSet">
                                <i class="fas fa-spinner fa-spin" v-if="savingSet"></i>
                                <i class="far fa-save" v-if="!savingSet"></i>
                                {{ $t('buttons.save.btnText') }}
                            </button>

                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-5" v-if="Array.isArray(criteriaSource) && criteriaSource.length > 0">
                <div class="card-body">
                    <div v-cloak>
                        <div class="input-group col-sm-3 float-right">
                            <div class="input-group-addon fa fa-search" style="display:table-cell"></div>
                            <input class="form-control fa fa-search" name="query" v-model="searchQuery">
                        </div>
                    </div>

                    <custom-grid :data="criteriaSource"
                                 :columns="gridColumns"
                                 :filter-key="searchQuery"
                                 :captions="gridCaptions"
                                 :gridName="'EvaluationCriteria'"
                                 :btns="isLockedForEdit? []:criteriaSourceBtns">
                    </custom-grid>

                    <vs-prompt :title="$t('registers.events.evaluation.criteria.add')"
                               :accept-text="$t('buttons.ok')"
                               :cancel-text="$t('buttons.cancel')"
                               :active.sync="activePrompt"
                               :is-valid="validationSuccess"
                               @cancel="cancelPrompt"
                               @close="cancelPrompt"
                               @accept="pushCriteria"
                               color="primary">
                        <div v-if="isPropertyCriteria">
                            <div class="form-group row">
                                <label for="criteriaCode" class="col-sm-2 col-form-label">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.code')}}</label>
                                <div class="col-sm-10">
                                    <input type="text" readonly class="form-control-plaintext"
                                           id="criteriaCode"
                                           :value="criteriaModel.code">
                                </div>
                            </div>

                            <div class="form-group row">
                                <label for="criteriaName" class="col-sm-2 col-form-label">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.name')}}</label>
                                <div class="col-sm-10">
                                    <input type="text" readonly class="form-control-plaintext"
                                           id="criteriaName"
                                           :value="isEnVersion ? criteriaModel.nameEn : criteriaModel.name">
                                </div>
                            </div>

                            <div class="form-group">
                                <label for="criteriaName" class="col-sm-2 col-form-label">
                                    {{ $t('registers.events.evaluation.criteria.type') }}
                                </label>
                                <kendo-dropdownlist v-model="criteriaModel.criteriaType"
                                                    :data-source="criteriaTypes"
                                                    :data-text-field="isEnVersion ? 'textEn'  : 'text'"
                                                    :data-value-field="'id'"
                                                    :filter="'contains'"
                                                    :optionLabel="$t('choose')"
                                                    :clear-button="true"
                                                    style="width: 100%;">
                                </kendo-dropdownlist>
                            </div>

                            <div v-if="isEvaluationCriteria" class="form-group">
                                <vs-input type="number"
                                          :min="0"
                                          :max="32767"
                                          :label="$t('registers.events.evaluation.criteria.score')"
                                          v-model="criteriaModel.score"></vs-input>
                                <span class="text-danger" v-if="!validScore">{{ $t('registers.events.evaluation.criteria.errors.invalidScore') }}</span>
                            </div>
                            <div class="clearfix"></div>

                            <div class="card">
                                <div class="card-body">
                                    <div class="form-group">
                                        <label for="form-fields" class="col-12 col-form-label">{{
                                            $t('registers.events.evaluation.criteria.property')}}</label>
                                        <kendo-multicolumncombobox :data-source="formFields"
                                                                   :data-text-field="isEnVersion ? 'displayTextEn' : 'displayText'"
                                                                   :data-value-field="'id'"
                                                                   :name="'form-fields'"
                                                                   :id="'form-fields'"
                                                                   :clear-button="true"
                                                                   v-model="criteriaModel.formFieldId"
                                                                   :placeholder="$t('selectProperty')"
                                                                   :no-data-template="$t('missingData')"
                                                                   :filter="'contains'"
                                                                   class="form-control"
                                                                   style="width:100%">
                                            <kendo-multicolumncombobox-column v-if="!isEnVersion"
                                                                              :field="'name'"
                                                                              :title="$t('editor.common.name')"
                                                                              :width="300">
                                            </kendo-multicolumncombobox-column>
                                            <kendo-multicolumncombobox-column v-if="isEnVersion"
                                                                              :field="'nameEn'"
                                                                              :title="$t('editor.common.nameEN')"
                                                                              :width="300">
                                            </kendo-multicolumncombobox-column>
                                            <kendo-multicolumncombobox-column :field="'fieldCode'"
                                                                              :title="$t('editor.common.fieldCode')"
                                                                              :width="300">
                                            </kendo-multicolumncombobox-column>
                                            <kendo-multicolumncombobox-column :field="'fieldType'"
                                                                              :title="$t('editor.common.type')"
                                                                              :width="150">
                                            </kendo-multicolumncombobox-column>

                                        </kendo-multicolumncombobox>
                                        <span class="text-danger" v-if="!validFormFieldValue">{{ $t('registers.events.evaluation.criteria.errors.invalidProperty') }}</span>

                                    </div>

                                    <div class="form-group">
                                        <kendo-datasource ref="operatorsDatasource"
                                                          :transport="operatorsTransportSettings"
                                                          :server-filtering="true"
                                                          :schema-data="'items'"
                                                          :error="dataSourceError">
                                        </kendo-datasource>

                                        <label for="operators" class="col-12 col-form-label">{{
                                            $t('registers.events.evaluation.criteria.operator')}}</label>
                                        <kendo-multicolumncombobox disabled="disabled"
                                                                   :data-source-ref="'operatorsDatasource'"
                                                                   :auto-bind="false"
                                                                   :cascade-from="'form-fields'"
                                                                   :data-text-field="'operatorName'"
                                                                   :data-value-field="'operatorName'"
                                                                   :name="'operators'"
                                                                   :id="'operators'"
                                                                   :clear-button="true"
                                                                   v-model="criteriaModel.operator"
                                                                   :placeholder="$t('choose')"
                                                                   :no-data-template="$t('missingData')"
                                                                   :filter="'contains'"
                                                                   style="width:100%">
                                            <kendo-multicolumncombobox-column :field="'operatorName'"
                                                                              :title="$t('editor.common.name')"
                                                                              :width="150">
                                            </kendo-multicolumncombobox-column>
                                            <kendo-multicolumncombobox-column :field="'description'"
                                                                              :title="$t('description')"
                                                                              :width="200">
                                            </kendo-multicolumncombobox-column>

                                        </kendo-multicolumncombobox>
                                        <span class="text-danger" v-if="!validOperatorValue">{{ $t('registers.events.evaluation.criteria.errors.invalidOperator') }}</span>
                                    </div>

                                    <div v-if="isTextFormField || isDropdownFormField" class="form-group">
                                        <vs-input :label="$t('registers.events.evaluation.criteria.value')"
                                                  v-model="criteriaModel.textValue"></vs-input>
                                        <span class="text-danger" v-if="!validTextValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                                    </div>
                                    <div v-else-if="isDatetimeFormField" class="form-group">
                                        <span class="vs-select--label">{{ $t('registers.events.evaluation.criteria.date') }}</span>
                                        <datetime-picker v-model="criteriaModel.datetimeValue" :showOnlyYear="false"
                                                         :showTime="true"></datetime-picker>
                                        <span class="text-danger" v-if="!validDateValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                                    </div>
                                    <div v-else-if="isToggleFormField" class="form-group">
                                        <div>
                                            <span class=" vs-select--label">{{ $t('registers.events.evaluation.criteria.value') }}</span>
                                        </div>
                                        <div class="float-left mt-2">
                                            <vs-checkbox v-model="criteriaModel.booleanValue"></vs-checkbox>
                                        </div>
                                    </div>
                                    <div v-else-if="isDecimalFormField || isIntegerFormField" class="form-group">
                                        <vs-input type="number"
                                                  :label="$t('registers.events.evaluation.criteria.value')"
                                                  v-model="criteriaModel.decimalValue"></vs-input>
                                        <span class="text-danger" v-if="!validDecimalValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div v-else>
                            <div class="form-group row">
                                <label for="criteriaCode" class="col-sm-2 col-form-label">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.code')}}</label>
                                <div class="col-sm-10">
                                    <input type="text" readonly class="form-control-plaintext"
                                           id="criteriaCode"
                                           :value="criteriaModel.code">
                                </div>
                            </div>

                            <div class="form-group row">
                                <label for="criteriaName" class="col-sm-2 col-form-label">{{
                                    $t('registers.events.evaluation.criteria.gridHeaders.name')}}</label>
                                <div class="col-sm-10">
                                    <input type="text" readonly class="form-control-plaintext"
                                           id="criteriaName"
                                           :value="isEnVersion ? criteriaModel.nameEn : criteriaModel.name">
                                </div>
                            </div>

                            <div class="form-group">
                                <label for="criteriaName" class="col-sm-2 col-form-label">
                                    {{ $t('registers.events.evaluation.criteria.type') }}
                                </label>
                                <kendo-dropdownlist v-model="criteriaModel.criteriaType"
                                                    :data-source="criteriaTypes"
                                                    :data-text-field="isEnVersion ? 'textEn'  : 'text'"
                                                    :data-value-field="'id'"
                                                    :filter="'contains'"
                                                    :optionLabel="$t('choose')"
                                                    :clear-button="true"
                                                    style="width: 100%;">
                                </kendo-dropdownlist>
                            </div>

                            <div v-if="isNumberCriteria || isNumberDateCriteria || isNumberYearCriteria"
                                 class="form-group">
                                <vs-input type="number"
                                          :label="$t('registers.events.evaluation.criteria.value')"
                                          v-model="criteriaModel.decimalValue"></vs-input>
                                <span class="text-danger" v-if="!validDecimalValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                            </div>

                            <div v-if="isDateCriteria || isNumberDateCriteria || isNumberYearCriteria"
                                 class="form-group">
                                <span v-if="isDateCriteria" class="vs-select--label">{{ $t('registers.events.evaluation.criteria.date') }}</span>
                                <span v-else-if="isNumberDateCriteria" class="vs-select--label">{{ $t('registers.events.evaluation.criteria.date') }}</span>
                                <span v-else class="vs-select--label">{{ $t('registers.events.evaluation.criteria.year') }}</span>
                                <datetime-picker v-model="criteriaModel.datetimeValue"
                                                 :showOnlyYear="isNumberYearCriteria"
                                                 :showTime="false"></datetime-picker>
                                <span class="text-danger" v-if="!validDateValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                            </div>

                            <div v-if="isBooleanCriteria" class="form-group">
                                <div>
                                    <span class=" vs-select--label">{{ $t('registers.events.evaluation.criteria.value') }}</span>
                                </div>
                                <div class="float-left mt-2">
                                    <vs-checkbox v-model="criteriaModel.booleanValue"></vs-checkbox>
                                </div>
                            </div>

                            <div v-if="isTextCriteria" class="form-group">
                                <vs-input :label="$t('registers.events.evaluation.criteria.value')"
                                          v-model="criteriaModel.textValue"></vs-input>
                                <span class="text-danger" v-if="!validTextValue">{{ $t('registers.events.evaluation.criteria.errors.invalidValue') }}</span>
                            </div>

                            <div v-if="isEvaluationCriteria" class="form-group">
                                <vs-input type="number"
                                          :min="0"
                                          :max="32767"
                                          :label="$t('registers.events.evaluation.criteria.score')"
                                          v-model="criteriaModel.score"></vs-input>
                                <span class="text-danger" v-if="!validScore">{{ $t('registers.events.evaluation.criteria.errors.invalidScore') }}</span>
                            </div>
                            <div class="clearfix"></div>
                        </div>
                    </vs-prompt>
                </div>
            </div>
        </div>
    </div>
    <div class="px-3" v-else>
        <h4>Details</h4>
        <h4>{{eventId}}</h4>
    </div>
</template>

<script>
    import {EvaluationCriteriaSetModel, EvaluationCriteriaModel} from '../../models/models';
    import {EvaluationCriteriaType} from '../../models/enums';

    import CustomGrid from "../../components/grid/CustomGrid";
    import Criteria from "./Criteria";
    import DatetimePicker from '../../components/datepicker/DatePicker';

    export default {
        inject: ['$validator'],
        props: {
            eventId: {
                type: Number,
                required: true
            },
            details: {
                type: Boolean,
                default: false
            },
            isLockedForEdit: {
                type: Boolean,
                default: false
            }
        },
        components: {
            CustomGrid,
            DatetimePicker,
            Criteria
        },
        data() {
            return {
                criteriaSet: {
                    type: EvaluationCriteriaSetModel
                },
                criteriaModel: {
                    type: EvaluationCriteriaModel
                },
                useTemplate: false,
                setLoading: false,
                selectedTemplateId: 0,
                criteriaTypes: [],
                formFields: [],
                criteriaSource: [],
                operators: [],
                searchQuery: '',
                gridColumns: ['code', 'name', 'nameEn', 'note'],
                editableColumns: [],
                gridCaptions: {
                    id: 'registers.events.evaluation.criteria.gridHeaders.id',
                    code: 'registers.events.evaluation.criteria.gridHeaders.code',
                    name: 'registers.events.evaluation.criteria.gridHeaders.name',
                    nameEn: 'registers.events.evaluation.criteria.gridHeaders.nameEn',
                    note: 'registers.events.evaluation.criteria.gridHeaders.note',
                },
                criteriaSourceBtns: [{
                    name: "add",
                    text: "",
                    tooltip: this.$t('buttons.add.btnText'),
                    class: 'btn btn-primary btn-lg',
                    icon: "fas fa-plus",
                    show: true,
                    clickHandler: this.addCriteria
                }],
                activePrompt: false,
                saveActivePrompt: false,
                deleteActivePrompt: false,
                criteriaToDelete: {},
                savingSet: false,
                deletingCriteria: false,
                operatorsTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/evaluation/operatorsForField`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
            }
        },
        methods: {
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap(options, operation) {
                if (operation !== 'read' && options.models) {
                    return {models: kendo.stringify(options.models)}
                } else {
                    // Викаме POST action за листване на номенклатурите и трябва да сериализираме
                    // options. Така се предава информация за take, skip , page и списък с обекти
                    // описващи сортирането
                    return kendo.stringify(options);
                }
            },
            loadCriteriSetByEvent() {
                this.setLoading = true;

                this.$http.get(`/api/evaluation/loadSetByEvent/${this.eventId}`).then(result => {
                    if (result.data) {
                        this.criteriaSet = new EvaluationCriteriaSetModel(result.data);
                    } else {
                        this.criteriaSet = new EvaluationCriteriaSetModel();
                        this.criteriaSet.registerEntityId = this.eventId;
                    }
                    this.setLoading = false;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                    this.setLoading = false;
                });
            },
            loadCriteriSetById() {
                this.setLoading = true;
                this.$http.get(`/api/evaluation/loadSet/${this.selectedTemplateId}`).then(result => {
                    this.criteriaSet = new EvaluationCriteriaSetModel(result.data);
                    this.setLoading = false;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                    this.setLoading = false;
                });
            },
            loadCriterisTypes() {
                this.$http.get(`/api/evaluation/criteriaTypes`).then(result => {
                    this.criteriaTypes = result.data;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            loadFormFields() {
                this.$http.get(`/api/evaluation/formFieldsForRequestToParticipateForm`, {
                    params: {
                        eventId: this.eventId
                    }
                }).then(result => {
                    this.formFields = result.data;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            loadOperators() {
                this.$http.get(`/api/evaluation/operators`).then(result => {
                    this.operators = result.data;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            loadCriteriaSource() {
                this.$http.get(`/api/evaluation/source`).then(result => {
                    this.criteriaSource = result.data;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            loadSetClick() {
                if (!this.selectedTemplateId) {
                    this.$vs.notify({
                        title: '',
                        text: this.$t('invalidAction'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.loadCriteriSetById();
            },
            addCriteria(args) {
                this.showEditPrompt(args)
            },
            showEditPrompt(args) {
                this.criteriaModel = new EvaluationCriteriaModel(args);
                this.activePrompt = true;
            },
            cancelPrompt() {
                this.criteriaModel = new EvaluationCriteriaModel();
            },
            pushCriteria() {
                if (this.canSaveSet) {
                    this.criteriaSet.criteria.push(this.criteriaModel)
                    this.cancelPrompt();

                    return;
                }

                let toAdd = this.criteriaSet;
                toAdd.criteria = []
                toAdd.criteria.push(this.criteriaModel);

                //Save to the DB
                this.$http.post('/api/evaluation/addCriteria', toAdd).then((response) => {
                    this.$vs.notify({
                        title: this.$t('buttons.save.btnText'),
                        text: this.$helpers.responseMessage(response),
                        color: 'success',
                        time: this.$store.getters.notificationDuration,
                    })
                    this.cancelPrompt();
                    this.loadCriteriSetByEvent();

                }).catch(error => {
                    this.cancelPrompt();

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                })
            },
            deleteCriteria(criteria) {
                kendo.confirm(`${this.$t('buttons.delete.confirmationText')}`)
                    .then(function () {
                        this.criteriaToDelete = criteria;
                        this.doDeleteCriteria()

                        if (criteria.id > 0) {
                            this.criteriaSet.criteria = this.criteriaSet.criteria.filter(c => c.id !== criteria.id);
                        }

                    }.bind(this));
            },
            doDeleteCriteria() {
                if (this.canSaveSet) {
                    if (this.criteriaToDelete.id > 0) {
                        this.criteriaSet.criteria = this.criteriaSet.criteria.filter(c => c.id !== this.criteriaToDelete.id);
                    }

                    this.criteriaToDelete = {};
                    return;
                }

                this.$http.delete('/api/evaluation/deleteCriteria', {
                        params: {
                            criteriaToCriteriaSetId: this.criteriaToDelete.criteriaToCriteriaSetId,
                            eventId: this.eventId
                        }
                    }
                ).then((response) => {
                    this.$vs.notify({
                        title: this.$t('buttons.delete.action'),
                        text: this.$helpers.responseMessage(response),
                        color: 'success',
                        time: this.$store.getters.notificationDuration,
                    })

                    this.criteriaToDelete = {};
                    this.loadCriteriSetByEvent();

                }).catch(error => {
                    this.criteriaToDelete = {};

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })

                })

            },
            saveCriteriaSet() {
                kendo.confirm(`${this.$t('buttons.save.confirmationText')}`)
                    .then(function () {
                        this.doSaveCriteriaSet();
                    }.bind(this));
            },
            doSaveCriteriaSet() {
                if (!this.canSaveSet) {
                    // Cannot save
                    this.$vs.notify({
                        title: this.$t('buttons.save.btnText'),
                        text: this.$t('invalidAction'),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.savingSet = true;
                this.$http.post('/api/evaluation/saveCriteriaSet', this.criteriaSet).then((response) => {
                    //������� �� CriteriaSetModel
                    let returnModel = response.data.criteriaSet;
                    if (!returnModel) {
                        this.$vs.notify({
                            title: this.$t('buttons.save.btnText'),
                            text: 'Empty result model!',
                            color: 'warning',
                            time: this.$store.getters.notificationDuration,
                        })
                    } else {
                        this.criteriaSet = new EvaluationCriteriaSetModel(response.data.criteriaSet);

                        this.$vs.notify({
                            title: this.$t('buttons.save.btnText'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    }

                    this.savingSet = false;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                    this.savingSet = false;
                })
            }
        },
        mounted() {
            if (this.isNewEvent) {
                this.criteriaSet = new EvaluationCriteriaSetModel();
            } else {
                this.loadCriteriSetByEvent();
            }

            this.loadCriteriaSource();
            this.loadCriterisTypes();
            this.loadFormFields();
            this.loadOperators();
        },
        computed: {
            isNewEvent() {
                return isNaN(this.eventId) || this.eventId === undefined || this.eventId === 0;
            },
            token: function () {
                return this.$store.getters.authToken;
            },
            isNewCriteriaSet() {
                return this.criteriaSet.id === 0;
            },
            validationSuccess() {
                return this.criteriaModel.id > 0 && this.criteriaModel.criteriaType > 0;
            },
            validScore() {
                let val = parseInt(this.criteriaModel.score);
                return this.criteriaModel.criteriaType === EvaluationCriteriaType.Admissibility
                    || (!isNaN(val) && val >= 0 && val <= 32767);
            },
            validDecimalValue() {
                let val = Number(this.criteriaModel.decimalValue);
                return !isNaN(val);
            },
            validBooleanValue() {
                let val = this.criteriaModel.booleanValue;
                return val === true || val === false || val === 'true' || val === 'false';
            },
            validDateValue() {
                return true;
            },
            validTextValue() {
                return this.criteriaModel.textValue;
            },
            validOperatorValue() {
                let val = this.criteriaModel.operator;
                return val != null && val.length > 0;
            },
            validFormFieldValue() {
                let val = Number(this.criteriaModel.formFieldId);
                return !isNaN(val) && val > 0;
            },
            isAdmissibilityCriteria() {
                let val = parseInt(this.criteriaModel.criteriaType);
                return !isNaN(val) && val === EvaluationCriteriaType.Admissibility;
            },
            isEvaluationCriteria() {
                let val = parseInt(this.criteriaModel.criteriaType);
                return !isNaN(val) && val === EvaluationCriteriaType.Evaluation;
            },
            isNumberCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('n')
                    && code.startsWith('nd') === false
                    && code.startsWith('ny') === false;
            },
            isNumberDateCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('nd');
            },
            isNumberYearCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('ny');
            },
            isDateCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('d');
            },
            isBooleanCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('b');
            },
            isTextCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('s');
            },
            isPropertyCriteria() {
                if (!this.criteriaModel.code) {
                    return false;
                }

                let code = this.criteriaModel.code.toLowerCase();
                return code && code.startsWith('p');
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === 'en';
            },
            hasCriteriaItems() {
                return Array.isArray(this.criteriaSet.criteria) && this.criteriaSet.criteria.length > 0
            },
            canSaveSet() {
                return this.isNewCriteriaSet && !this.isNewEvent
            },
            isDatetimeFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'datepicker';
            },
            isToggleFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'toggle';
            },
            isDropdownFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'dropdown';
            },
            isIntegerFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'input'
                    && formField[0].inputType.toLowerCase() === 'number'
                    && formField[0].numberType.toLowerCase() === 'int';
            },
            isDecimalFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'input'
                    && formField[0].inputType.toLowerCase() === 'number'
                    && formField[0].numberType.toLowerCase() === 'decimal';
            },
            isTextFormField() {
                let formFieldId = Number(this.criteriaModel.formFieldId)
                let formField = this.formFields.filter(x => x.id === formFieldId);
                return formField && formField.length > 0 && formField[0].fieldType.toLowerCase() === 'input'
                    && formField[0].inputType.toLowerCase() === 'text';
            }
        }
    }
</script>
